import { ref, computed, watch } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import store from '@/store'
import { ulid } from 'ulid'

function debounce(fn, ms) {
  let timer
  const debounced = (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), ms)
  }
  debounced.cancel = () => clearTimeout(timer)
  return debounced
}

async function fetchV2Cart(bottleId) {
  const token = await store.dispatch('getToken', {
    subdomain: window.location.host
  })

  const res = await fetch(`${process.env.VUE_APP_API_BASE_URL}/merchant/v2/bottles/${bottleId}/cart`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: 'Bearer ' + token
    }
  })

  if (!res.ok) throw new Error(res.status)

  return res.json()
}

export async function postV2Cart(bottleId, body, signal) {
  const token = await store.dispatch('getToken', {
    subdomain: window.location.host
  })

  const res = await fetch(`${process.env.VUE_APP_API_BASE_URL}/merchant/v2/bottles/${bottleId}/cart`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: 'Bearer ' + token
    },
    body: JSON.stringify(body)
  })

  if (!res.ok) throw new Error(res.status)

  return res.json()
}

// Transform local cart item shape to POST body shape
// Local stores flat: [{ variant_option_id, variant_id, quantity }]
// Backend expects nested: [{ id: variant_id, variant_options: [{ id: variant_option_id, quantity }] }]
export function toPostShape(items) {
  return items
    .filter((item) => item.quantity > 0)
    .map((item) => ({
      category_product_store_pair_id: item.category_product_store_pair_id,
      quantity: item.quantity,
      variants: groupByVariant(item.cart_item_variant_options_attributes || [])
    }))
}

// Group flat variant options by variant_id into nested structure
function groupByVariant(attrs) {
  const grouped = {}
  attrs.forEach((attr) => {
    const vid = attr.variant_id
    if (!grouped[vid]) grouped[vid] = { id: vid, variant_options: [] }
    grouped[vid].variant_options.push({ id: attr.variant_option_id, quantity: attr.quantity || 1 })
  })
  return Object.values(grouped)
}

// Map server cart items back to local shape (with cart_item_variant_options_attributes)
// Server returns nested: variants[].variant_options[] — flatten to flat array
// Each item gets a frontend_key for unique identification (supports multiple customizations of same product)
function toLocalShape(serverItems) {
  return (serverItems || []).map((item, index) => ({
    frontend_key: `${item.category_product_store_pair_id}-${index}-${Date.now()}`,
    category_product_store_pair_id: item.category_product_store_pair_id,
    quantity: item.quantity,
    product_inventory_remaining: item.product_inventory_remaining,
    store_inventory_remaining: item.store_inventory_remaining,
    cart_item_variant_options_attributes: (item.variants || []).flatMap((v) =>
      (v.variant_options || []).map((vo) => ({
        variant_option_id: vo.id,
        variant_id: v.id,
        quantity: vo.quantity || 1,
        inventory_remaining: vo.inventory_remaining
      }))
    )
  }))
}

// Preserve frontend_keys from existing local items when syncing with server response
// Matches items positionally per cpsp_id so component keys stay stable
function preserveKeys(existingItems, newItems) {
  const existingByCpsp = {}
  existingItems.forEach((item) => {
    const cpsp = String(item.category_product_store_pair_id)
    if (!existingByCpsp[cpsp]) existingByCpsp[cpsp] = []
    existingByCpsp[cpsp].push(item.frontend_key)
  })

  const usedByCpsp = {}
  newItems.forEach((item) => {
    const cpsp = String(item.category_product_store_pair_id)
    if (!usedByCpsp[cpsp]) usedByCpsp[cpsp] = 0
    const keys = existingByCpsp[cpsp] || []
    const idx = usedByCpsp[cpsp]
    if (idx < keys.length) {
      item.frontend_key = keys[idx]
    }
    usedByCpsp[cpsp] = idx + 1
  })
}

// [BACKEND_FORMAT_NEEDED] Client-side price formatting until backend returns formatted objects
export function formatPrice(cents, currency = 'USD') {
  if (cents == null) return ''
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100)
}

export function useCartMutation(bottleId) {
  const queryClient = useQueryClient()
  const resolvedBottleId = computed(() => (typeof bottleId === 'function' ? bottleId() : bottleId?.value ?? bottleId))

  // Optimistic local cart items (drives quantity/variant display in components)
  const localCartItems = ref([])
  const isSubmittingCart = ref(false)
  let abortController = null
  let lastServerCartItems = []

  const cartQuery = useQuery({
    queryKey: computed(() => ['v2-cart', resolvedBottleId.value]),
    queryFn: () => fetchV2Cart(resolvedBottleId.value),
    enabled: computed(() => !!resolvedBottleId.value),
    staleTime: Infinity
  })

  // Seed localCartItems from GET response. The seed happens once, tracked with a flag rather than
  // inferred from `localCartItems.length === 0`: a line added while the GET was still in flight
  // made the local cart non-empty, so the server's items were dropped and the next POST sent that
  // one line as the customer's entire cart.
  let cartSeeded = false
  watch(
    () => cartQuery.data.value,
    (data) => {
      if (cartSeeded || !data?.cart?.cart_items) return
      cartSeeded = true

      const serverItems = toLocalShape(data.cart.cart_items)
      const pending = localCartItems.value.filter(
        (item) => !serverItems.some((serverItem) => serverItem.frontend_key === item.frontend_key)
      )
      localCartItems.value = [...serverItems, ...pending]
      lastServerCartItems = JSON.parse(JSON.stringify(serverItems))
    },
    { immediate: true }
  )

  // True once the GET has come back (or errored, or was never enabled). Anything that has to
  // reconcile against the customer's existing lines - the deep-link modal restore, above all - has
  // to wait for this, or the same URL opens a different modal depending on network timing.
  const isCartSettled = computed(() => !resolvedBottleId.value || cartQuery.isFetched.value)

  // Computed values from server cache
  const v2Cart = computed(() => cartQuery.data.value?.cart ?? null)
  const v2CartItems = computed(() => v2Cart.value?.cart_items ?? [])
  const v2CartErrors = computed(() => ({
    cart_errors: v2Cart.value?.cart_errors ?? [],
    inventory_errors: v2Cart.value?.inventory_errors ?? [],
    slot_errors: v2Cart.value?.slot_errors ?? [],
    discount_errors: v2Cart.value?.discount_errors ?? []
  }))
  const v2PricingTier = computed(() => v2Cart.value?.pricing_tier ?? null)
  const v2NextPricingTier = computed(() => v2Cart.value?.next_pricing_tier ?? null)
  const v2CartTotal = computed(() => v2Cart.value?.total_formatted ?? null)
  const v2CartQuantity = computed(() => v2Cart.value?.quantity ?? 0)

  // Collect all previously answered questions from the cart cache. Entries the
  // server marked `unasked` (auto-answered via should_ask_for_*, no real
  // answer) must not be echoed back — resubmitting them would turn them into
  // committable nil-value answers.
  function collectAnsweredQuestions() {
    return (v2Cart.value?.answers || [])
      .filter((a) => a.answered && !a.unasked)
      .map((a) => ({ question_id: a.question_id, value: a.value, values: a.values, skipped: a.skipped }))
  }

  // Build the full POST body — always includes cart_items + answers.
  // package_id falls back to the currently active package from the last cart
  // response, so cart mutations after a package was selected don't drop it.
  // When the caller explicitly passes packageId (including null for a
  // deselect from V2PackageSelection's toggle), send it on the wire so the
  // backend can distinguish "deselect" from "no change".
  function buildPostBody({ packageId, answers } = {}) {
    const explicitPackageId = packageId !== undefined
    const effectivePackageId = explicitPackageId ? packageId : v2Cart.value?.package_id
    const body = {
      cart_items: toPostShape(localCartItems.value),
      answers: answers || collectAnsweredQuestions()
    }
    if (explicitPackageId || effectivePackageId != null) {
      body.package_id = effectivePackageId
    }
    return body
  }

  // Submit cart and sync response back to local state + TanStack Query cache
  async function submitAndSync(body) {
    if (abortController) abortController.abort()
    abortController = new AbortController()

    isSubmittingCart.value = true
    const response = await postV2Cart(resolvedBottleId.value, body, abortController.signal)

    queryClient.setQueryData(['v2-cart', resolvedBottleId.value], response)

    if (response?.cart?.cart_items) {
      // A POST round trip is a stronger seed than the GET: it reflects the cart *after* this
      // change, so a slower GET issued before it must not be allowed to seed over the top.
      cartSeeded = true
      const serverItems = toLocalShape(response.cart.cart_items)
      preserveKeys(localCartItems.value, serverItems)
      localCartItems.value = serverItems
      lastServerCartItems = JSON.parse(JSON.stringify(serverItems))
    }

    isSubmittingCart.value = false
    return response
  }

  async function submitNow() {
    try {
      await submitAndSync(buildPostBody())
    } catch (e) {
      if (e.name === 'AbortError') return
      localCartItems.value = JSON.parse(JSON.stringify(lastServerCartItems))
      isSubmittingCart.value = false
    }
  }

  // Debounced POST to backend
  const debouncedSubmit = debounce(submitNow, 300)

  // Update a cart item — called by V2 components via inject('v2UpdateCartItem')
  // Matches by frontend_key to support multiple customizations of the same product.
  //
  // `immediate` skips the 300ms debounce. The debounce exists to coalesce a run of stepper clicks;
  // the product modal sends the whole configuration in one deliberate press, so waiting only made
  // the basket footer lag behind the confirmation toast the customer had already seen.
  function updateCartItem(submittableCartItem, { immediate = false } = {}) {
    const key = submittableCartItem.frontend_key
    const cpspId = submittableCartItem.category_product_store_pair_id
    const items = [...localCartItems.value]
    const index = key
      ? items.findIndex((item) => item.frontend_key === key)
      : items.findIndex((item) => String(item.category_product_store_pair_id) === String(cpspId))

    if (submittableCartItem.quantity === 0) {
      if (index !== -1) items.splice(index, 1)
    } else if (index !== -1) {
      items[index] = {
        frontend_key: key || items[index].frontend_key,
        category_product_store_pair_id: cpspId,
        quantity: submittableCartItem.quantity,
        cart_item_variant_options_attributes: submittableCartItem.cart_item_variant_options_attributes || []
      }
    } else {
      items.push({
        frontend_key: key || ulid(),
        category_product_store_pair_id: cpspId,
        quantity: submittableCartItem.quantity,
        cart_item_variant_options_attributes: submittableCartItem.cart_item_variant_options_attributes || []
      })
    }

    localCartItems.value = items
    if (immediate) {
      debouncedSubmit.cancel()
      submitNow()
    } else {
      debouncedSubmit()
    }
  }

  // Set package and re-submit cart immediately (non-debounced)
  async function setPackageId(packageId) {
    try {
      await submitAndSync(buildPostBody({ packageId }))
    } catch (e) {
      if (e.name === 'AbortError') return
      localCartItems.value = JSON.parse(JSON.stringify(lastServerCartItems))
      isSubmittingCart.value = false
    }
  }

  // Answer a question via the V2 cart POST endpoint (immediate, non-debounced).
  // packageId is set for package questions so the same POST also applies the
  // selection (autofill/minimums/pricing). The answers array alone never feeds
  // bottle.package_id until commit. undefined leaves the active package untouched.
  async function answerQuestion({ question_id, value, values, skipped, packageId }) {
    try {
      const previousAnswers = collectAnsweredQuestions().filter((a) => a.question_id !== question_id)
      const answers = [...previousAnswers, { question_id, value, values, skipped }]
      return await submitAndSync(buildPostBody({ packageId, answers }))
    } catch (e) {
      if (e.name === 'AbortError') return
      localCartItems.value = JSON.parse(JSON.stringify(lastServerCartItems))
      isSubmittingCart.value = false
    }
  }

  // Commit the cart to the database before navigating to checkout.
  // On a non-abort failure the spinner must be released here — no later
  // request will reset it, so a missed catch leaves the footer loader
  // spinning forever. AbortError keeps it on: a newer request owns the flag.
  async function commitCart() {
    debouncedSubmit.cancel()
    const body = buildPostBody()
    body.commit = true
    try {
      return await submitAndSync(body)
    } catch (e) {
      if (e.name !== 'AbortError') isSubmittingCart.value = false
      throw e
    }
  }

  // Adjust a no-longer-available cluster item. These items are NOT real lines
  // in localCartItems (the snapshot filters them out), so the shared
  // updateCartItem can't match them — it would no-op a removal or push a
  // phantom duplicate. Instead submit the cart with this cpsp re-attached at
  // the new quantity (the backend surfaces submitted-but-unavailable cpsps in
  // unavailable_cart_items), or send the explicit removal signal at zero so
  // the backend drops it from the prior-cache merge for good. localCartItems
  // is never mutated, so no phantom available-grid line appears.
  async function updateUnavailableCartItem({
    category_product_store_pair_id,
    quantity,
    cart_item_variant_options_attributes = []
  }) {
    debouncedSubmit.cancel()
    const cpspId = category_product_store_pair_id
    const body = buildPostBody()
    body.cart_items = body.cart_items.filter((ci) => String(ci.category_product_store_pair_id) !== String(cpspId))
    if (quantity > 0) {
      body.cart_items.push({
        category_product_store_pair_id: cpspId,
        quantity,
        variants: groupByVariant(cart_item_variant_options_attributes || [])
      })
    } else {
      body.removed_unavailable_cart_item_cpsp_ids = [cpspId]
    }
    try {
      return await submitAndSync(body)
    } catch (e) {
      if (e.name === 'AbortError') return
      localCartItems.value = JSON.parse(JSON.stringify(lastServerCartItems))
      isSubmittingCart.value = false
    }
  }

  // Add a new customization of the same product (fresh variant selections)
  function addCustomization(cpspId, { isUpsell = false } = {}) {
    const items = [...localCartItems.value]
    items.push({
      frontend_key: ulid() + (isUpsell ? '-upsell' : ''),
      category_product_store_pair_id: cpspId,
      quantity: 1,
      cart_item_variant_options_attributes: []
    })
    localCartItems.value = items
    debouncedSubmit()
  }

  // Find server cart item for a product by cpsp_id (for strike-through pricing)
  function cartItemForProduct(cpspId) {
    return v2CartItems.value.find((item) => String(item.category_product_store_pair_id) === String(cpspId)) ?? null
  }

  return {
    v2Cart,
    v2CartItems,
    v2CartErrors,
    v2PricingTier,
    v2NextPricingTier,
    v2CartTotal,
    v2CartQuantity,
    isSubmittingCart: computed(() => isSubmittingCart.value),
    isCartSettled,
    localCartItems,
    updateCartItem,
    updateUnavailableCartItem,
    addCustomization,
    setPackageId,
    answerQuestion,
    commitCart,
    cartItemForProduct
  }
}
